import { DEFAULT_PAGE_SIZE } from "@/core/constants/pagination";
import type { HttpRequestConfig, IHttpClient } from "@/core/interfaces";
import {
  collectAllPages,
  extractItems,
  extractTotal,
  isRecord,
  readFirstText,
  toPaginatedQuery,
} from "@/core/utils";
import { Registry } from "@/shared/infra/dependency-injection/registry";
import type {
  CreatePapelObraParams,
  InactivatePapelObraParams,
  ListPapeisObraParams,
  PapeisObraPage,
  PapelObra,
  PapelObraJanelaRequest,
  PapelObraMarco,
  UpdatePapelObraParams,
} from "./papeis-obra.types";

/** Contrato consumido do hub-bff (HU 2033/2034). */
export const PAPEIS_OBRA_PATHS = {
  LIST: "/api/recursos/papeis-obra",
} as const;

const MARCOS: readonly PapelObraMarco[] = [
  "ANTES_INICIO_OBRA",
  "DEPOIS_INICIO_OBRA",
  "ANTES_TERMINO_OBRA",
  "DEPOIS_TERMINO_OBRA",
];

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

function toDias(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function toMarco(value: unknown): PapelObraMarco | undefined {
  return MARCOS.find((marco) => marco === value);
}

/** Lê a janela do bff; `dias` e `marco` chegam nulos quando acompanha o início/término. */
function toJanela(raw: unknown): PapelObraJanelaRequest | null {
  if (!isRecord(raw) || !isRecord(raw.entrada) || !isRecord(raw.saida)) return null;

  return {
    entrada: {
      inicioDaObra: raw.entrada.inicioDaObra === true,
      dias: toDias(raw.entrada.dias),
      marco: toMarco(raw.entrada.marco),
    },
    saida: {
      terminoDaObra: raw.saida.terminoDaObra === true,
      dias: toDias(raw.saida.dias),
      marco: toMarco(raw.saida.marco),
    },
  };
}

function toPapelObra(raw: unknown): PapelObra | null {
  if (!isRecord(raw)) return null;

  const id = readFirstText(raw, "id").trim();
  if (!id) return null;

  return {
    id,
    nome: readFirstText(raw, "nome").trim(),
    obrigatorio: raw.obrigatorio === true,
    ativo: raw.ativo !== false,
    aplicado: raw.aplicado === true,
    janela: toJanela(raw.janela),
  };
}

/** Consulta dos papéis na obra: busca, paginação 1-based e ordenação no contrato paginado. */
export async function listPapeisObra(request: ListPapeisObraParams = {}): Promise<PapeisObraPage> {
  const { busca, page = 1, pageSize = DEFAULT_PAGE_SIZE, sort, signal } = request;

  const params: Record<string, string | number> = { ...toPaginatedQuery({ page, pageSize, sort }) };
  const term = busca?.trim();
  if (term) params.busca = term;

  const config: HttpRequestConfig = { signal, params };
  const response = await getHttpClient().get<unknown>(PAPEIS_OBRA_PATHS.LIST, config);

  const papeis = extractItems(response.data)
    .map(toPapelObra)
    .filter((papel): papel is PapelObra => papel !== null);

  return { papeis, total: extractTotal(response.data) ?? papeis.length };
}

/** Todos os papéis ativos (percorre as páginas), para montar a equipe de uma obra. */
export async function listAllPapeisObra(signal?: AbortSignal): Promise<PapelObra[]> {
  const papeis = await collectAllPages(async (page, pageSize) => {
    const result = await listPapeisObra({ page, pageSize, signal });
    return { items: result.papeis, total: result.total };
  });

  return papeis.filter((papel) => papel.ativo);
}

/**
 * Cadastra um papel na obra. `janela` só é enviada quando o papel é obrigatório.
 * Retorna o papel criado ou `null` se a resposta não trouxer corpo utilizável.
 */
export async function createPapelObra({
  nome,
  obrigatorio,
  janela,
  signal,
}: CreatePapelObraParams): Promise<PapelObra | null> {
  const body: Record<string, unknown> = { nome, obrigatorio };
  if (obrigatorio && janela) body.janela = janela;

  const response = await getHttpClient().post<unknown>(PAPEIS_OBRA_PATHS.LIST, body, { signal });

  return toPapelObra(isRecord(response.data) ? response.data.data : null);
}

/** Caminho da inativação de um papel (HU 2037/2269). */
export function buildInactivatePapelObraPath(id: string): string {
  return `${PAPEIS_OBRA_PATHS.LIST}/${encodeURIComponent(id)}/inativacao`;
}

/** Inativa o papel (sem excluí-lo), enviando a justificativa exigida pela RN06. */
export async function inactivatePapelObra({
  id,
  justificativa,
  signal,
}: InactivatePapelObraParams): Promise<void> {
  await getHttpClient().patch<unknown>(
    buildInactivatePapelObraPath(id),
    { justificativa },
    { signal },
  );
}

/**
 * Edita um papel na obra (PUT). Mesmo corpo do cadastro; `janela` só quando obrigatório.
 * Retorna o papel atualizado ou `null` se a resposta não trouxer corpo utilizável.
 */
export async function updatePapelObra({
  id,
  nome,
  obrigatorio,
  janela,
  signal,
}: UpdatePapelObraParams): Promise<PapelObra | null> {
  const body: Record<string, unknown> = { nome, obrigatorio };
  if (obrigatorio && janela) body.janela = janela;

  const response = await getHttpClient().put<unknown>(
    `${PAPEIS_OBRA_PATHS.LIST}/${encodeURIComponent(id)}`,
    body,
    { signal },
  );

  return toPapelObra(isRecord(response.data) ? response.data.data : null);
}
